import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  AdsClick,
  Audiotrack,
  CalendarToday,
  Description,
  Download,
  Edit as EditIcon,
  Link as LinkIcon,
  Fullscreen,
  FullscreenExit,
  Image,
  Mic,
  MicOff,
  Movie,
  OpenInNew,
  People,
  RadioButtonChecked,
  Stop,
  VideoCall,
  Videocam,
  VideocamOff,
  Warning,
} from '@mui/icons-material'

import { useAppSelector } from '../hooks/redux'
import { hasPermission } from '../utils/permissions'
import {
  useGetCourtroomByRoundQuery,
  useJoinCourtroomMutation,
  useStartCourtroomMutation,
  useEndCourtroomMutation,
  useSetRecordingMutation,
  useStartRecordingMutation,
  useStopRecordingMutation,
  useConfigureTimerMutation,
  usePauseTimerMutation,
  useResumeTimerMutation,
  useGetTimerStateQuery,
  useGetCourtroomPresenceQuery,
  useSubmitVerdictMutation,
  usePublishVerdictMutation,
  useGetVerdictQuery,
  useCreateCourtroomMutation,
  useShareEvidenceMutation,
  useStopSharingMutation,
  useKickParticipantMutation,
  useUnkickParticipantMutation,
  useGetMeetingTokenMutation,
  useMuteParticipantMutation,
  useUnmuteParticipantMutation,
  useInjectSurpriseMutation,
  useSendPointerEventMutation,
  useStopPointerEventMutation,
  CourtroomStatus,
} from '../store/api/courtroomsApi'
import { useGetCaseRoundsQuery, RoundType } from '../store/api/roundsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import {
  DocumentType,
  useGetDocumentsQuery,
  useLazyDownloadDocumentQuery,
  useLazyDownloadDocumentAsPdfQuery,
} from '../store/api/documentsApi'
import {
  useGetMemorialsQuery,
  useLazyDownloadMemorialQuery,
  useLazyDownloadMemorialAsPdfQuery,
} from '../store/api/memorialsApi'
import {
  useGetEvidenceQuery,
  useLazyDownloadEvidenceQuery,
  useLazyDownloadEvidenceAsPdfQuery,
} from '../store/api/evidenceApi'
import { useGetUsersQuery } from '../store/api/usersApi'
import { buildApiUrl, resolveApiBaseUrl } from '../config/api'
import { formatCaseDisplay } from '../utils/caseDisplay'
import { COURTROOM_IDLE_MS } from '../config/courtroomIdle'
import { useIdleDailyDisconnect } from '../hooks/useIdleDailyDisconnect'
import { gradientBrand } from '../theme/brand'
import { PageHeader } from '../components/PageHeader'
import { CourtroomTimelinePanel } from '../components/CourtroomTimelinePanel'
import { ModeratorQAPanel } from '../components/ModeratorQAPanel'
import { PdfPresentationViewer } from '../components/PdfPresentationViewer'
import { getCardSurfaceSx, getContentPaperSx, getFilterPaperSx } from '../theme/sectionSurfaces'

type CourtAssetType = 'document' | 'memorial' | 'evidence'
type CourtAsset = {
  id: string
  sourceType: CourtAssetType
  title: string
  fileName: string
  mimeType: string
  uploadedAt?: string
  teamId?: string
  size?: number
}
type ParticipantInfo = {
  userId: string
  displayName: string
  roleLabel: string
  avatarText: string
  micOn: boolean
  camOn: boolean
  canKick: boolean
  canMute: boolean
  isMuted: boolean
}
type PointerState = {
  active: boolean
  x: number
  y: number
  byUserId: string
  byRole: string
}

const STATUS_LABELS: Record<CourtroomStatus, string> = {
  [CourtroomStatus.SCHEDULED]: 'Đã lên lịch',
  [CourtroomStatus.ACTIVE]: 'Đang diễn ra',
  [CourtroomStatus.COMPLETED]: 'Đã kết thúc',
  [CourtroomStatus.CANCELLED]: 'Đã hủy',
}

const STATUS_COLORS: Record<
  CourtroomStatus,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  [CourtroomStatus.SCHEDULED]: 'info',
  [CourtroomStatus.ACTIVE]: 'success',
  [CourtroomStatus.COMPLETED]: 'default',
  [CourtroomStatus.CANCELLED]: 'error',
}

function inferMimeTypeFromName(fileName: string): string {
  const lower = fileName.toLowerCase()
  if (lower.endsWith('.pdf')) return 'application/pdf'
  if (lower.endsWith('.png')) return 'image/png'
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg'
  if (lower.endsWith('.webp')) return 'image/webp'
  if (lower.endsWith('.gif')) return 'image/gif'
  if (lower.endsWith('.mp4')) return 'video/mp4'
  if (lower.endsWith('.webm')) return 'video/webm'
  if (lower.endsWith('.mp3')) return 'audio/mpeg'
  if (lower.endsWith('.wav')) return 'audio/wav'
  if (lower.endsWith('.ogg')) return 'audio/ogg'
  if (lower.endsWith('.docx')) {
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  }
  return 'application/octet-stream'
}

function formatBytes(size?: number): string {
  if (!size || size <= 0) return '-'
  if (size < 1024) return `${size} B`
  const kb = size / 1024
  if (kb < 1024) return `${kb.toFixed(1)} KB`
  const mb = kb / 1024
  return `${mb.toFixed(1)} MB`
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/)
  if (!parts.length) return 'U'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

function detectMeetingProvider(meetingLink?: string): 'daily' | 'legacy' | 'other' {
  if (!meetingLink) return 'other'
  const lower = meetingLink.trim().toLowerCase()
  // Một số môi trường dùng URL tương đối hoặc copy thiếu scheme — vẫn nhận diện Daily.
  if (lower.includes('daily.co/') || lower.includes('.daily.co')) return 'daily'
  try {
    const hostname = new URL(meetingLink).hostname.toLowerCase()
    if (hostname.includes('daily.co')) return 'daily'
    if (hostname.includes('meet.google.com')) return 'legacy'
  } catch {
    return lower.includes('daily') ? 'daily' : 'other'
  }
  return 'legacy'
}

function buildDailyJoinUrl(
  meetingLink: string,
  identity?: { id?: string; displayName?: string } | string,
): string {
  if (!meetingLink) return meetingLink
  // Backward-compat: nếu caller truyền string đơn → coi như id (legacy).
  const ident =
    typeof identity === 'string'
      ? { id: identity, displayName: identity }
      : identity
  try {
    const url = new URL(meetingLink)
    if (!url.hostname.toLowerCase().includes('daily.co')) return meetingLink
    if (ident?.id) url.searchParams.set('user_id', ident.id)
    // userName: dùng display name thật để participant thấy tên đầy đủ trong room.
    if (ident?.displayName) url.searchParams.set('userName', ident.displayName)
    return url.toString()
  } catch {
    return meetingLink
  }
}

export default function CourtroomPage() {
  const theme = useTheme()
  const isCompact = useMediaQuery(theme.breakpoints.down('md'))

  const { user: currentUser, token } = useAppSelector((state) => state.auth)
  const { data: cases = [] } = useGetCasesQuery()
  const { data: teams = [] } = useGetTeamsQuery()
  const { data: users = [] } = useGetUsersQuery()

  const [selectedCaseId, setSelectedCaseId] = useState('')
  const [selectedRoundId, setSelectedRoundId] = useState('')
  const [recordingDialogOpen, setRecordingDialogOpen] = useState(false)
  const [endDialogOpen, setEndDialogOpen] = useState(false)
  const [verdictDialogOpen, setVerdictDialogOpen] = useState(false)
  const [recordingUrl, setRecordingUrl] = useState('')
  const [mediationResult, setMediationResult] = useState<'mediation_success' | 'mediation_failed' | 'postponed'>('mediation_success')
  const [mediationConclusion, setMediationConclusion] = useState('')
  const [mediationAttachmentId, setMediationAttachmentId] = useState('')
  const [verdictSummary, setVerdictSummary] = useState('')
  const [verdictDetailHtml, setVerdictDetailHtml] = useState('')
  const [verdictWinnerTeamId, setVerdictWinnerTeamId] = useState('')
  const [verdictPenalty, setVerdictPenalty] = useState('')
  const [verdictLegalBasis, setVerdictLegalBasis] = useState('')
  const [verdictAttachmentId, setVerdictAttachmentId] = useState('')
  const [selectedCourtroomId, setSelectedCourtroomId] = useState('')
  const [newHostJudgeId, setNewHostJudgeId] = useState('')
  const [newSessionNotes, setNewSessionNotes] = useState('')

  const [activePanelTab, setActivePanelTab] = useState(0)
  const [splitRatio, setSplitRatio] = useState(42)
  const [draggingDivider, setDraggingDivider] = useState(false)
  const splitWrapRef = useRef<HTMLDivElement | null>(null)

  const [selectedAssetId, setSelectedAssetId] = useState('')
  const [previewObjectUrl, setPreviewObjectUrl] = useState('')
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState('')

  // Banner hiển thị "Tình tiết bất ngờ" realtime (BE inject qua /inject-surprise → SSE)
  const [surpriseAlert, setSurpriseAlert] = useState<{
    actorName: string
    statement: string
    targetTeamId?: string | null
    sentAt: string
  } | null>(null)

  // Fullscreen — riêng cho evidence preview + meet embed.
  const previewWrapRef = useRef<HTMLDivElement | null>(null)
  const meetWrapRef = useRef<HTMLDivElement | null>(null)
  // (DOCX preview cũ qua mammoth FE-side đã được thay bằng BE convert sang PDF
  // qua LibreOffice headless — fidelity cao hơn, hỗ trợ pptx/odt/rtf/xlsx.)

  // Track element nào đang fullscreen để swap icon + apply sx khác.
  // (document.fullscreenElement chỉ là DOM API, React không re-render khi thay đổi —
  // cần listen fullscreenchange event và lưu state.)
  const [fullscreenEl, setFullscreenEl] = useState<'preview' | 'meet' | null>(null)

  const toggleFullscreen = (target: 'preview' | 'meet') => {
    const el = target === 'preview' ? previewWrapRef.current : meetWrapRef.current
    if (!el) return
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => {})
    } else {
      void el.requestFullscreen().catch(() => {})
    }
  }

  useEffect(() => {
    const onChange = () => {
      const fs = document.fullscreenElement
      if (!fs) {
        setFullscreenEl(null)
      } else if (fs === previewWrapRef.current) {
        setFullscreenEl('preview')
      } else if (fs === meetWrapRef.current) {
        setFullscreenEl('meet')
      }
      // Khi vào/ra fullscreen, Daily iframe cần resize lại theo viewport mới.
      // Daily SDK không tự re-layout — trigger window resize event để buộc nó tính lại.
      window.dispatchEvent(new Event('resize'))
    }
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  const canManageCourtrooms = !!(currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES'))

  const { data: rounds = [] } = useGetCaseRoundsQuery(selectedCaseId, {
    skip: !selectedCaseId,
  })
  const onlineHearingRounds = rounds.filter((r) => r.round_type === RoundType.SEMIFINAL)

  useEffect(() => {
    if (cases.length > 0 && !selectedCaseId) {
      setSelectedCaseId(cases[0].id)
    }
  }, [cases, selectedCaseId])

  useEffect(() => {
    if (onlineHearingRounds.length > 0 && selectedCaseId && !selectedRoundId) {
      setSelectedRoundId(onlineHearingRounds[0].id)
    }
  }, [onlineHearingRounds, selectedCaseId, selectedRoundId])

  const {
    data: courtroom,
    isLoading: isLoadingCourtroom,
    refetch: refetchCourtroom,
  } = useGetCourtroomByRoundQuery(selectedRoundId, {
    skip: !selectedRoundId,
    pollingInterval: 0,
  })

  // Khớp BE `_can_control_session` (courtroom_service.py): admin OR
  // (judge AND (host_judge_id == user.id OR host_judge_id chưa gán → bất kỳ judge nào)).
  // Dùng cho: start/end/pause-resume/configure timer, start/stop recording, submit_verdict.
  // Phải khai sau `courtroom` query (TDZ — const không hoisted).
  const canControlSession = (() => {
    if (!currentUser) return false
    if (canManageCourtrooms) return true
    if (currentUser.role !== 'judge') return false
    if (!courtroom) return false
    if (!courtroom.host_judge_id) return true  // fallback: chưa gán host → bất kỳ judge
    return courtroom.host_judge_id === currentUser.id
  })()

  const [joinCourtroom, { isLoading: isJoining }] = useJoinCourtroomMutation()
  const [startCourtroom, { isLoading: isStarting }] = useStartCourtroomMutation()
  const [endCourtroom, { isLoading: isEnding }] = useEndCourtroomMutation()
  const [setRecording, { isLoading: isSettingRecording }] = useSetRecordingMutation()
  const [startRecording, { isLoading: isStartingRecording }] = useStartRecordingMutation()
  const [stopRecording, { isLoading: isStoppingRecording }] = useStopRecordingMutation()
  const [configureTimer, { isLoading: isConfiguringTimer }] = useConfigureTimerMutation()
  const [pauseTimer, { isLoading: isPausingTimer }] = usePauseTimerMutation()
  const [resumeTimer, { isLoading: isResumingTimer }] = useResumeTimerMutation()
  const [submitVerdict, { isLoading: isSubmittingVerdict }] = useSubmitVerdictMutation()
  const [publishVerdict, { isLoading: isPublishingVerdict }] = usePublishVerdictMutation()
  const [createCourtroom, { isLoading: isCreatingCourtroom }] = useCreateCourtroomMutation()
  const [shareEvidence, { isLoading: isSharingEvidence }] = useShareEvidenceMutation()
  const [stopSharing, { isLoading: isStoppingSharing }] = useStopSharingMutation()
  const [kickParticipant, { isLoading: isKickingParticipant }] = useKickParticipantMutation()
  const [unkickParticipant] = useUnkickParticipantMutation()
  const [muteParticipant] = useMuteParticipantMutation()
  const [unmuteParticipant] = useUnmuteParticipantMutation()
  const [getMeetingToken] = useGetMeetingTokenMutation()
  const [injectSurprise, { isLoading: isInjectingSurprise }] = useInjectSurpriseMutation()
  // UAT-Chung kết #14: BTC inject tình tiết bất ngờ
  const [surpriseOpen, setSurpriseOpen] = useState(false)
  const [surpriseActor, setSurpriseActor] = useState('')
  const [surpriseStatement, setSurpriseStatement] = useState('')
  const [surpriseTargetTeam, setSurpriseTargetTeam] = useState('')
  const [kickDialogOpen, setKickDialogOpen] = useState(false)
  const [kickReason, setKickReason] = useState('')
  const [participantToKick, setParticipantToKick] = useState<ParticipantInfo | null>(null)
  const [sessionDurationMinutes, setSessionDurationMinutes] = useState(45)
  const [displayRemainingSeconds, setDisplayRemainingSeconds] = useState<number | null>(null)
  const [pointerMode, setPointerMode] = useState(false)
  const [remotePointer, setRemotePointer] = useState<PointerState | null>(null)
  const [dailyEmbedFailed, setDailyEmbedFailed] = useState(false)
  const [dailyMeetingJoined, setDailyMeetingJoined] = useState(false)
  const pointerHideTimerRef = useRef<number | null>(null)
  const pointerLastSentRef = useRef(0)
  const previewSurfaceRef = useRef<HTMLDivElement | null>(null)
  const dailyEmbedRef = useRef<HTMLDivElement | null>(null)
  const dailyCallFrameRef = useRef<any>(null)

  const { data: caseDocuments = [] } = useGetDocumentsQuery(
    { case_id: selectedCaseId || undefined },
    { skip: !selectedCaseId }
  )
  const { data: memorials = [] } = useGetMemorialsQuery(
    { case_id: selectedCaseId || undefined },
    { skip: !selectedCaseId }
  )
  const { data: evidenceRows = [] } = useGetEvidenceQuery(
    { case_id: selectedCaseId || undefined },
    { skip: !selectedCaseId }
  )

  const [downloadDocument] = useLazyDownloadDocumentQuery()
  const [downloadMemorial] = useLazyDownloadMemorialQuery()
  const [downloadEvidence] = useLazyDownloadEvidenceQuery()
  // PDF variants — BE convert Office docs sang PDF qua LibreOffice headless.
  const [downloadDocumentAsPdf] = useLazyDownloadDocumentAsPdfQuery()
  const [downloadMemorialAsPdf] = useLazyDownloadMemorialAsPdfQuery()
  const [downloadEvidenceAsPdf] = useLazyDownloadEvidenceAsPdfQuery()
  const [sendPointerEvent] = useSendPointerEventMutation()
  const [stopPointerEvent] = useStopPointerEventMutation()
  const { data: timerState, refetch: refetchTimerState } = useGetTimerStateQuery(courtroom?.id || '', {
    skip: !courtroom?.id,
    pollingInterval: 10000,
  })
  const { data: verdict } = useGetVerdictQuery(courtroom?.id || '', {
    skip: !courtroom?.id || courtroom?.status !== CourtroomStatus.COMPLETED,
  })
  const isDailyMeetingLink = (courtroom?.meeting_link || '').toLowerCase().includes('daily.co')
  const { data: dailyPresence } = useGetCourtroomPresenceQuery(courtroom?.id || '', {
    skip: !courtroom?.id || !isDailyMeetingLink || !courtroom?.meeting_link,
    pollingInterval: 5000,
  })

  const selectedCase = cases.find((c) => c.id === selectedCaseId)
  const meetingProvider = useMemo(
    () => detectMeetingProvider(courtroom?.meeting_link),
    [courtroom?.meeting_link]
  )
  const canEmbedMeeting = meetingProvider === 'daily'
  const joinButtonLabel = meetingProvider === 'daily' ? 'Mở phòng Daily (tab mới)' : 'Mở link phòng (tab mới)'

  // CR#3.6 — Pre-check kick state để FE block join (khớp BE guard kicked_participants[me]).
  // Trước đó BE 403 muộn → UX rất xấu. Giờ FE biết trước, hiển thị nút disabled + tooltip.
  const myKickInfo = currentUser?.id ? courtroom?.kicked_participants?.[currentUser.id] : undefined
  const isUserKicked = myKickInfo?.status === 'kicked'

  const getDailyCallFrame = useCallback(() => dailyCallFrameRef.current ?? null, [])
  const idleDailyDisconnect = useIdleDailyDisconnect({
    enabled:
      !!courtroom &&
      courtroom.status === CourtroomStatus.ACTIVE &&
      canEmbedMeeting &&
      !dailyEmbedFailed &&
      isDailyMeetingLink &&
      dailyMeetingJoined,
    getCallFrame: getDailyCallFrame,
    warnAfterMs: COURTROOM_IDLE_MS.warnAfterMs,
    graceAfterWarnMs: COURTROOM_IDLE_MS.graceAfterWarnMs,
  })

  const assets = useMemo<CourtAsset[]>(() => {
    const caseContentDocs = caseDocuments
      .filter((d) =>
        [DocumentType.CASE_DOCUMENT, DocumentType.REFERENCE, DocumentType.EVIDENCE, DocumentType.MEMORIAL].includes(
          d.document_type
        )
      )
      .map((d) => ({
        id: d.id,
        sourceType: 'document' as const,
        title: d.title || d.file_name,
        fileName: d.file_name,
        mimeType: d.mime_type || inferMimeTypeFromName(d.file_name),
        uploadedAt: d.created_at,
        teamId: d.team_id,
        size: d.file_size,
      }))

    const memorialAssets = memorials.map((m) => ({
      id: m.id,
      sourceType: 'memorial' as const,
      title: m.title || m.file_name,
      fileName: m.file_name,
      mimeType: inferMimeTypeFromName(m.file_name),
      uploadedAt: m.submitted_at,
      teamId: m.team_id,
      size: m.file_size,
    }))

    const evidenceAssets = evidenceRows.map((e) => ({
      id: e.id,
      sourceType: 'evidence' as const,
      title: e.title || e.file_name,
      fileName: e.file_name,
      mimeType: e.mime_type || inferMimeTypeFromName(e.file_name),
      uploadedAt: e.uploaded_at,
      teamId: e.team_id,
      size: e.file_size,
    }))

    return [...caseContentDocs, ...memorialAssets, ...evidenceAssets].sort((a, b) => {
      const ta = a.uploadedAt ? new Date(a.uploadedAt).getTime() : 0
      const tb = b.uploadedAt ? new Date(b.uploadedAt).getTime() : 0
      return tb - ta
    })
  }, [caseDocuments, memorials, evidenceRows])

  const selectedAsset = useMemo(
    () => assets.find((x) => x.id === selectedAssetId) || null,
    [assets, selectedAssetId]
  )

  useEffect(() => {
    if (assets.length > 0 && !selectedAssetId) setSelectedAssetId(assets[0].id)
    if (assets.length === 0 && selectedAssetId) setSelectedAssetId('')
  }, [assets, selectedAssetId])

  useEffect(() => {
    return () => {
      if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl)
    }
  }, [previewObjectUrl])

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!draggingDivider || !splitWrapRef.current) return
      const rect = splitWrapRef.current.getBoundingClientRect()
      if (!rect.width) return
      const pct = ((e.clientX - rect.left) / rect.width) * 100
      setSplitRatio(Math.max(28, Math.min(70, pct)))
    }
    const stopDrag = () => setDraggingDivider(false)
    window.addEventListener('mousemove', handleMove)
    window.addEventListener('mouseup', stopDrag)
    return () => {
      window.removeEventListener('mousemove', handleMove)
      window.removeEventListener('mouseup', stopDrag)
    }
  }, [draggingDivider])

  useEffect(() => {
    if (!courtroom?.id || !token) return

    // Single EventSource, được khởi tạo bên trong connect() sau khi resolve API URL.
    // (Trước đây có một EventSource "ma" tạo ngay đầu effect leak vì không gắn
    // listener và không close khi connect() reassign biến `es` — đã xoá.)
    let es: EventSource | null = null
    let cancelled = false

    const onUpdated = () => {
      refetchCourtroom()
    }
    const onPointer = (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data || '{}')
        if (payload?.type !== 'courtroom.pointer') return
        if (!payload.active) {
          setRemotePointer(null)
          return
        }
        setRemotePointer({
          active: true,
          x: Number(payload.x || 0),
          y: Number(payload.y || 0),
          byUserId: String(payload.by_user_id || ''),
          byRole: String(payload.by_role || ''),
        })
        if (pointerHideTimerRef.current) {
          window.clearTimeout(pointerHideTimerRef.current)
        }
        pointerHideTimerRef.current = window.setTimeout(() => {
          setRemotePointer(null)
        }, 2000)
      } catch {
        // ignore malformed pointer payload
      }
    }

    const onQaEvent = () => {
      // Invalidate Q&A queries để moderator panel + public watch refetch.
      // RTK Query tag invalidation — không phụ thuộc payload.
      try {
        // dispatch invalidation qua dynamic import để tránh circular dep
        import('../store/api/apiSlice').then(({ apiSlice }) => {
          if (courtroom?.id) {
            // Invalidate cả 2 list (moderation queue + approved public list).
            void (apiSlice as any).util?.invalidateTags?.([
              { type: 'Courtroom', id: `moderation-qa-${courtroom.id}` },
              { type: 'Courtroom', id: `public-qa-${courtroom.id}` },
            ])
          }
        })
      } catch {
        /* noop */
      }
    }

    const onSurprise = (e: MessageEvent) => {
      try {
        const payload = JSON.parse(e.data || '{}')
        const actorName = String(payload.actor_name || 'BTC')
        const statement = String(payload.statement || '')
        if (!statement) return
        setSurpriseAlert({
          actorName,
          statement,
          targetTeamId: payload.target_team_id || null,
          sentAt: String(payload.sent_at || new Date().toISOString()),
        })
      } catch {
        // ignore malformed
      }
    }

    const connect = async () => {
      const apiBaseUrl = await resolveApiBaseUrl()
      if (cancelled) return

      const streamUrl = buildApiUrl(
        apiBaseUrl,
        `/courtrooms/${courtroom.id}/events?token=${encodeURIComponent(token)}`
      )
      es = new EventSource(streamUrl)
      es.addEventListener('courtroom.updated', onUpdated)
      es.addEventListener('courtroom.pointer', onPointer)
      es.addEventListener('courtroom.surprise_event', onSurprise)
      es.addEventListener('courtroom.qa_submitted', onQaEvent)
      es.addEventListener('courtroom.qa_moderated', onQaEvent)
      // Presentation events — refetch courtroom để FE sync page/annotations/presenter
      es.addEventListener('courtroom.page_changed', onUpdated)
      es.addEventListener('courtroom.annotation_added', onUpdated)
      es.addEventListener('courtroom.annotation_cleared', onUpdated)
      es.addEventListener('courtroom.presenter_changed', onUpdated)
      es.onerror = () => {
        // EventSource sẽ tự reconnect; giữ yên lặng để tránh nhiễu UX
      }
    }

    void connect()

    return () => {
      cancelled = true
      if (es) {
        es.removeEventListener('courtroom.updated', onUpdated)
        es.removeEventListener('courtroom.pointer', onPointer)
        es.removeEventListener('courtroom.surprise_event', onSurprise)
        es.removeEventListener('courtroom.qa_submitted', onQaEvent)
        es.removeEventListener('courtroom.qa_moderated', onQaEvent)
        es.removeEventListener('courtroom.page_changed', onUpdated)
        es.removeEventListener('courtroom.annotation_added', onUpdated)
        es.removeEventListener('courtroom.annotation_cleared', onUpdated)
        es.removeEventListener('courtroom.presenter_changed', onUpdated)
        es.close()
      }
    }
  }, [courtroom?.id, token, refetchCourtroom])

  /** Đổi phiên courtroom → bỏ trạng thái lỗi embed của phiên trước */
  useEffect(() => {
    setDailyEmbedFailed(false)
  }, [courtroom?.id])

  useLayoutEffect(() => {
    const embedHostReady = !isCompact || activePanelTab === 1

    const cleanupFrame = async () => {
      setDailyMeetingJoined(false)
      if (!dailyCallFrameRef.current) return
      try {
        await dailyCallFrameRef.current.leave()
      } catch {
        // ignore leave failures
      }
      try {
        await dailyCallFrameRef.current.destroy()
      } catch {
        // ignore destroy failures
      }
      dailyCallFrameRef.current = null
    }

    if (!canEmbedMeeting || !courtroom?.meeting_link || !embedHostReady) {
      void cleanupFrame()
      setDailyEmbedFailed(false)
      return
    }

    let cancelled = false
    let rafAttempts = 0
    let rafHandle = 0

    const initFrame = async () => {
      await cleanupFrame()
      if (cancelled || !dailyEmbedRef.current) return
      try {
        setDailyEmbedFailed(false)
        const DailyIframe = (await import('@daily-co/daily-js')).default
        const orphan = DailyIframe.getCallInstance?.()
        if (orphan && orphan !== dailyCallFrameRef.current) {
          try {
            await orphan.destroy()
          } catch {
            /* duplicate-instance / stale */
          }
        }
        if (cancelled || !dailyEmbedRef.current) return
        const callFrame = DailyIframe.createFrame(dailyEmbedRef.current, {
          showLeaveButton: true,
          iframeStyle: {
            width: '100%',
            height: '420px',
            border: '0',
          },
        })
        dailyCallFrameRef.current = callFrame
        callFrame.on('left-meeting', () => {
          setDailyMeetingJoined(false)
        })
        const displayName =
          currentUser?.full_name?.trim() ||
          currentUser?.username?.trim() ||
          currentUser?.id ||
          undefined

        // SECURITY: BE token endpoint reject kicked/completed/cancelled. Nếu BE
        // trả 403/400 → KHÔNG fallback join không token (trước đây cho join public
        // → user bị kick / phiên đã end vẫn vào được room). Token issue chỉ chấp
        // nhận khi là lỗi cấu hình BE (503 DAILY_API_KEY chưa set).
        let dailyToken: string | undefined
        try {
          const tokenRes = await getMeetingToken(courtroom.id).unwrap()
          dailyToken = tokenRes?.token || undefined
        } catch (err: any) {
          const status = err?.status
          const detail = err?.data?.detail || err?.message || 'Không cấp được token'
          if (status === 503) {
            // BE chưa cấu hình Anthropic/Daily → cho phép thử join public.
            console.warn('Daily token endpoint not configured — fallback to public join', err)
          } else {
            // 403 (kicked/no permission) hoặc 400 (status completed/cancelled) → block.
            if (!cancelled) {
              setDailyEmbedFailed(true)
              setDailyMeetingJoined(false)
              alert(`Không thể vào phòng: ${detail}`)
            }
            return
          }
        }

        await callFrame.join({
          url: buildDailyJoinUrl(courtroom.meeting_link, {
            id: currentUser?.id,
            displayName,
          }),
          userName: displayName,
          ...(dailyToken ? { token: dailyToken } : {}),
        })
        if (!cancelled) {
          setDailyMeetingJoined(true)
        }
      } catch (error) {
        console.error('Failed to initialize Daily call frame', error)
        setDailyEmbedFailed(true)
        setDailyMeetingJoined(false)
      }
    }

    const runWhenHostMounted = () => {
      if (cancelled) return
      if (dailyEmbedRef.current) {
        void initFrame()
        return
      }
      if (rafAttempts < 12) {
        rafAttempts += 1
        rafHandle = requestAnimationFrame(runWhenHostMounted)
      } else if (!cancelled) {
        setDailyEmbedFailed(true)
      }
    }

    runWhenHostMounted()

    return () => {
      cancelled = true
      if (rafHandle) cancelAnimationFrame(rafHandle)
      setDailyMeetingJoined(false)
      void cleanupFrame()
    }
  }, [
    activePanelTab,
    canEmbedMeeting,
    courtroom?.meeting_link,
    courtroom?.status,
    currentUser?.id,
    isCompact,
  ])

  useEffect(() => {
    return () => {
      if (pointerHideTimerRef.current) window.clearTimeout(pointerHideTimerRef.current)
    }
  }, [])

  const participantInfos = useMemo<ParticipantInfo[]>(() => {
    const ids = [...(courtroom?.participants || [])]
    const attendance = courtroom?.attendance || {}
    const presenceMap = new Map(
      (dailyPresence?.participants || []).map((p) => [String(p.user_id), p])
    )
    const usersById = new Map(users.map((u) => [u.id, u]))
    return ids.map((uid) => {
      const profile = usersById.get(uid)
      let displayName = profile?.full_name || profile?.username || `User ${uid.slice(0, 6)}`
      let roleLabel = 'Participant'
      if (courtroom?.host_judge_id && uid === courtroom.host_judge_id) {
        roleLabel = 'Judge (Host)'
      } else if (currentUser && uid === currentUser.id) {
        roleLabel = currentUser.role
        displayName = currentUser.full_name || currentUser.username || displayName
      } else {
        if (profile?.role) {
          roleLabel = profile.role
        }
        const foundTeam = teams.find((t) => t.members.includes(uid))
        if (foundTeam && !profile?.full_name) {
          displayName = `${foundTeam.name} - Thành viên`
          roleLabel = 'Student'
        }
      }
      const joined = !!attendance[uid]
      const live = courtroom?.status === CourtroomStatus.ACTIVE && joined
      const mediaState = presenceMap.get(uid)
      const canKick =
        !!currentUser &&
        uid !== currentUser.id &&
        (
          (!!courtroom?.host_judge_id &&
            currentUser.role === 'judge' &&
            courtroom.host_judge_id === currentUser.id &&
            uid !== courtroom.host_judge_id) ||
          canManageCourtrooms
        )
      // Quyền mute = quyền kick (host_judge / admin) — không cho mute chính mình.
      const canMute = canKick
      const isMuted = !!(courtroom?.muted_participants && courtroom.muted_participants[uid])
      return {
        userId: uid,
        displayName,
        roleLabel,
        avatarText: initials(displayName),
        micOn: mediaState?.mic_on ?? live,
        camOn: mediaState?.cam_on ?? live,
        canKick,
        canMute,
        isMuted,
      }
    })
  }, [courtroom, currentUser, teams, users, dailyPresence, canManageCourtrooms])

  const sharingPresenterLabel = useMemo(() => {
    const uid = courtroom?.shared_by_user_id
    if (!uid) return 'participant'
    const profile = users.find((u) => u.id === uid)
    if (profile) return profile.full_name || profile.username || profile.id
    const team = teams.find((t) => t.members.includes(uid))
    if (team) return team.name
    if (uid === currentUser?.id) return currentUser.full_name || currentUser.username || uid
    return uid
  }, [courtroom?.shared_by_user_id, teams, users, currentUser])

  const canShareEvidence = !!(
    currentUser &&
    (hasPermission(currentUser.role, 'VIEW_OWN_CASE') ||
      hasPermission(currentUser.role, 'ASSIGN_CASES') ||
      hasPermission(currentUser.role, 'SCORE_CASES'))
  )
  const canUsePointer = !!(
    currentUser &&
    currentUser.role === 'judge' &&
    courtroom?.status === CourtroomStatus.ACTIVE &&
    courtroom?.shared_evidence_active
  )
  const timerSeverity: 'success' | 'warning' | 'error' = useMemo(() => {
    const seconds = displayRemainingSeconds ?? timerState?.remaining_seconds ?? 0
    if (seconds <= 60) return 'error'
    if (seconds <= 300) return 'warning'
    return 'success'
  }, [displayRemainingSeconds, timerState?.remaining_seconds])
  const timerLabel = useMemo(() => {
    const base = displayRemainingSeconds ?? timerState?.remaining_seconds
    if (base === undefined || base === null) return '--:--'
    const remaining = Math.max(0, base)
    const mm = String(Math.floor(remaining / 60)).padStart(2, '0')
    const ss = String(remaining % 60).padStart(2, '0')
    const overtime = timerState?.overtime_seconds || 0
    if (overtime > 0) {
      const om = String(Math.floor(overtime / 60)).padStart(2, '0')
      const os = String(overtime % 60).padStart(2, '0')
      return `00:00 (+${om}:${os})`
    }
    return `${mm}:${ss}`
  }, [displayRemainingSeconds, timerState?.remaining_seconds, timerState?.overtime_seconds])

  useEffect(() => {
    if (!timerState) return
    setDisplayRemainingSeconds(timerState.remaining_seconds)
  }, [timerState?.remaining_seconds, timerState])

  useEffect(() => {
    if (displayRemainingSeconds === null) return
    // BR-T1 fix: dừng đếm khi BE báo timer is_paused — tránh UI lệch state server.
    // Khi resume, BE trả remaining_seconds mới + is_paused=false → effect chạy lại.
    if (timerState?.is_paused) return
    const id = window.setInterval(() => {
      setDisplayRemainingSeconds((prev) => {
        if (prev === null) return prev
        return Math.max(0, prev - 1)
      })
    }, 1000)
    return () => window.clearInterval(id)
  }, [displayRemainingSeconds !== null, timerState?.is_paused])

  useEffect(() => {
    if (!courtroom || !currentUser?.id) return
    const kicked = courtroom.kicked_participants?.[currentUser.id]
    if (kicked?.status === 'kicked') {
      alert(
        `Bạn đã bị loại khỏi phiên bởi Chủ tọa.${kicked.reason ? ` Lý do: ${kicked.reason}` : ''}`
      )
      window.location.href = '/'
    }
  }, [courtroom, currentUser?.id])

  // Auto self-mute khi BE đánh dấu muted_participants[me]. Daily SDK setLocalAudio(false).
  // Khi unmute → KHÔNG auto bật mic lại; user phải tự bật để giữ chủ động.
  useEffect(() => {
    if (!courtroom || !currentUser?.id) return
    const muted = courtroom.muted_participants?.[currentUser.id]
    if (muted?.status !== 'muted') return
    const frame = dailyCallFrameRef.current
    if (!frame) return
    try {
      frame.setLocalAudio(false)
    } catch (e) {
      console.warn('setLocalAudio failed', e)
    }
  }, [courtroom?.muted_participants, currentUser?.id])

  useEffect(() => {
    if (canUsePointer) return
    if (pointerMode) setPointerMode(false)
  }, [canUsePointer, pointerMode])

  const handleJoinCourtroom = async () => {
    if (!courtroom) return
    try {
      const result = await joinCourtroom(courtroom.id).unwrap()
      if (result.meeting_link) {
        const displayName =
          currentUser?.full_name?.trim() ||
          currentUser?.username?.trim() ||
          currentUser?.id ||
          undefined
        window.open(
          buildDailyJoinUrl(result.meeting_link, { id: currentUser?.id, displayName }),
          '_blank',
        )
      }
      refetchCourtroom()
    } catch (error: any) {
      alert(`Tham gia courtroom thất bại: ${error.data?.detail || error.message}`)
    }
  }

  /** Daily nhúng: không còn nút join mở tab — ghi nhận tham gia khi embed join xong. */
  useEffect(() => {
    if (!dailyMeetingJoined || !courtroom?.id) return
    let cancelled = false
    void (async () => {
      try {
        await joinCourtroom(courtroom.id).unwrap()
        if (!cancelled) refetchCourtroom()
      } catch {
        // im lặng — có thể đã join trước đó hoặc lỗi mạng tạm
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dailyMeetingJoined, courtroom?.id, joinCourtroom, refetchCourtroom])

  const handleEndCourtroom = async () => {
    if (!courtroom) return
    if (!mediationConclusion.trim()) {
      alert('Vui lòng nhập kết luận của Chủ tọa')
      return
    }
    try {
      await endCourtroom({
        courtroomId: courtroom.id,
        result: mediationResult,
        conclusion: mediationConclusion.trim(),
        attachment_document_id: mediationAttachmentId.trim() || undefined,
      }).unwrap()
      refetchCourtroom()
      setEndDialogOpen(false)
      alert('Đã lưu kết quả hòa giải và kết thúc phiên')
    } catch (error: any) {
      alert(`Kết thúc courtroom thất bại: ${error.data?.detail || error.message}`)
    }
  }

  const handleTogglePointerMode = async () => {
    if (!courtroom) return
    if (pointerMode) {
      setPointerMode(false)
      try {
        await stopPointerEvent(courtroom.id).unwrap()
      } catch {
        // silent fail to avoid UX noise
      }
      return
    }
    setPointerMode(true)
  }

  const handlePointerMove = async (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!pointerMode || !courtroom || !previewSurfaceRef.current) return
    const now = Date.now()
    if (now - pointerLastSentRef.current < 33) return
    pointerLastSentRef.current = now
    const rect = previewSurfaceRef.current.getBoundingClientRect()
    if (!rect.width || !rect.height) return
    const x = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
    const y = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height))
    try {
      await sendPointerEvent({
        courtroomId: courtroom.id,
        x,
        y,
        active: true,
      }).unwrap()
    } catch {
      // no-op
    }
  }

  const handleStartCourtroom = async () => {
    if (!courtroom) return
    try {
      await startCourtroom(courtroom.id).unwrap()
      refetchCourtroom()
    } catch (error: any) {
      alert(`Bắt đầu phiên thất bại: ${error.data?.detail || error.message}`)
    }
  }

  const handleOpenRecordingDialog = (courtroomId: string) => {
    setSelectedCourtroomId(courtroomId)
    setRecordingUrl('')
    setRecordingDialogOpen(true)
  }

  const handleStartRecording = async () => {
    if (!courtroom) return
    try {
      await startRecording(courtroom.id).unwrap()
      refetchCourtroom()
    } catch (error: any) {
      alert(`Bắt đầu ghi hình thất bại: ${error?.data?.detail || error?.message}`)
    }
  }

  const handleConfigureTimer = async () => {
    if (!courtroom) return
    try {
      await configureTimer({
        courtroomId: courtroom.id,
        duration_seconds: Math.max(1, sessionDurationMinutes) * 60,
      }).unwrap()
      refetchCourtroom()
      refetchTimerState()
    } catch (error: any) {
      alert(`Cấu hình timer thất bại: ${error?.data?.detail || error?.message}`)
    }
  }

  const handlePauseResumeTimer = async () => {
    if (!courtroom) return
    try {
      if (timerState?.is_paused) {
        await resumeTimer(courtroom.id).unwrap()
      } else {
        await pauseTimer(courtroom.id).unwrap()
      }
      refetchCourtroom()
      refetchTimerState()
    } catch (error: any) {
      alert(`Cập nhật timer thất bại: ${error?.data?.detail || error?.message}`)
    }
  }

  const handleSubmitVerdict = async () => {
    if (!courtroom) return
    if (!verdictSummary.trim() || !verdictDetailHtml.trim() || !verdictWinnerTeamId.trim()) {
      alert('Vui lòng nhập đủ dữ liệu phán quyết bắt buộc')
      return
    }
    if (!confirm('Bạn có chắc chắn với phán quyết này? Sau khi xác nhận sẽ không thể chỉnh sửa thường.')) return
    try {
      await submitVerdict({
        courtroomId: courtroom.id,
        summary: verdictSummary.trim(),
        detail_html: verdictDetailHtml.trim(),
        winner_team_id: verdictWinnerTeamId.trim(),
        penalty: verdictPenalty.trim() || undefined,
        legal_basis: verdictLegalBasis.trim() || undefined,
        attachment_document_id: verdictAttachmentId.trim() || undefined,
      }).unwrap()
      setVerdictDialogOpen(false)
      refetchCourtroom()
    } catch (error: any) {
      alert(`Lưu phán quyết thất bại: ${error?.data?.detail || error?.message}`)
    }
  }

  const handlePublishVerdict = async () => {
    if (!courtroom) return
    try {
      await publishVerdict(courtroom.id).unwrap()
      refetchCourtroom()
    } catch (error: any) {
      alert(`Công bố phán quyết thất bại: ${error?.data?.detail || error?.message}`)
    }
  }

  const handleStopRecording = async () => {
    if (!courtroom) return
    try {
      await stopRecording({
        courtroomId: courtroom.id,
        recording_url: courtroom.recording_url || undefined,
        recording_format: courtroom.recording_format || 'mp4',
      }).unwrap()
      refetchCourtroom()
    } catch (error: any) {
      alert(`Dừng ghi hình thất bại: ${error?.data?.detail || error?.message}`)
    }
  }

  const handleSetRecording = async () => {
    if (!recordingUrl.trim()) {
      alert('Vui lòng nhập recording URL')
      return
    }
    try {
      await setRecording({
        courtroomId: selectedCourtroomId,
        recordingUrl: recordingUrl.trim(),
      }).unwrap()
      setRecordingDialogOpen(false)
      refetchCourtroom()
      alert('Recording URL đã được cập nhật!')
    } catch (error: any) {
      alert(`Cập nhật recording thất bại: ${error.data?.detail || error.message}`)
    }
  }

  const handleShareEvidence = async (asset: CourtAsset) => {
    if (!courtroom) return
    try {
      await shareEvidence({
        courtroomId: courtroom.id,
        asset_id: asset.id,
        asset_type: asset.sourceType,
        title: asset.title,
        file_name: asset.fileName,
        mime_type: asset.mimeType,
      }).unwrap()
      refetchCourtroom()
    } catch (error: any) {
      alert(`Chia sẻ chứng cứ thất bại: ${error?.data?.detail || error?.message}`)
    }
  }

  const handleStopSharing = async () => {
    if (!courtroom) return
    try {
      await stopSharing(courtroom.id).unwrap()
      refetchCourtroom()
    } catch (error: any) {
      alert(`Dừng chia sẻ thất bại: ${error?.data?.detail || error?.message}`)
    }
  }

  const handleKickParticipant = async () => {
    if (!courtroom || !participantToKick) return
    try {
      await kickParticipant({
        courtroomId: courtroom.id,
        userId: participantToKick.userId,
        reason: kickReason.trim(),
      }).unwrap()
      setKickDialogOpen(false)
      setKickReason('')
      setParticipantToKick(null)
      refetchCourtroom()
    } catch (error: any) {
      alert(`Kick participant thất bại: ${error?.data?.detail || error?.message}`)
    }
  }

  const loadAssetPreview = async (asset: CourtAsset) => {
    setSelectedAssetId(asset.id)
    setPreviewError('')
    setPreviewLoading(true)
    if (previewObjectUrl) {
      URL.revokeObjectURL(previewObjectUrl)
      setPreviewObjectUrl('')
    }
    // Office docs (.docx/.pptx/.odt/.rtf/.xlsx) → BE convert sang PDF (cache theo
    // sha256). Hơn mammoth FE-side vì giữ fidelity (bảng, header, font).
    const mime = (asset.mimeType || '').toLowerCase()
    const name = (asset.fileName || '').toLowerCase()
    const isOfficeDoc =
      mime.includes('officedocument.') ||
      mime === 'application/msword' ||
      mime === 'application/vnd.ms-powerpoint' ||
      mime === 'application/vnd.ms-excel' ||
      mime.includes('opendocument') ||
      mime.includes('rtf') ||
      /\.(docx?|pptx?|odt|odp|ods|rtf|xlsx?|csv)$/i.test(name)
    try {
      let blob: Blob
      if (isOfficeDoc) {
        // Fetch trực tiếp PDF preview để render iframe (fidelity tốt nhất).
        if (asset.sourceType === 'document') {
          blob = await downloadDocumentAsPdf(asset.id).unwrap()
        } else if (asset.sourceType === 'memorial') {
          blob = await downloadMemorialAsPdf(asset.id).unwrap()
        } else {
          blob = await downloadEvidenceAsPdf(asset.id).unwrap()
        }
        // Override mime để renderPreview pick PDF branch.
        blob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' })
      } else if (asset.sourceType === 'document') {
        blob = await downloadDocument(asset.id).unwrap()
      } else if (asset.sourceType === 'memorial') {
        blob = await downloadMemorial(asset.id).unwrap()
      } else {
        blob = await downloadEvidence(asset.id).unwrap()
      }
      setPreviewObjectUrl(URL.createObjectURL(blob))
    } catch (error: any) {
      // BE trả Blob ngay cả khi lỗi (responseHandler='blob' ở download endpoints).
      // Khi error → error.data là Blob chứa JSON {detail: ...} → đọc text để surface.
      let detail = error?.data?.detail || error?.message
      if (error?.data instanceof Blob) {
        try {
          const text = await error.data.text()
          try {
            const parsed = JSON.parse(text)
            detail = parsed?.detail || parsed?.message || text
          } catch {
            detail = text || detail
          }
        } catch {
          /* ignore */
        }
      }
      const status = error?.status
      // 503 → BE chưa cài LibreOffice. Báo rõ ràng.
      if (status === 503 || (typeof detail === 'string' && detail.includes('LibreOffice'))) {
        setPreviewError(
          'Hệ thống chưa cài LibreOffice để convert tài liệu Office. Liên hệ admin để cài đặt.',
        )
      } else if (status === 403) {
        setPreviewError(detail || 'Bạn không có quyền xem tài liệu này.')
      } else if (status === 404) {
        setPreviewError(detail || 'Không tìm thấy file đính kèm (có thể đã bị xoá).')
      } else if (status === 415) {
        setPreviewError(detail || 'Định dạng file không hỗ trợ preview — tải về để xem.')
      } else {
        setPreviewError(detail || `Không thể tải preview tài liệu (mã ${status || 'unknown'}).`)
      }
    } finally {
      setPreviewLoading(false)
    }
  }

  const renderPreview = () => {
    if (!selectedAsset) {
      return (
        <Box sx={{ p: 2, flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Chọn tài liệu bên trái để xem nội dung.
          </Typography>
        </Box>
      )
    }
    const origMime = selectedAsset.mimeType.toLowerCase()
    const origName = (selectedAsset.fileName || '').toLowerCase()
    // Office docs đã được BE convert sang PDF qua loadAssetPreview → ép mime PDF.
    const wasConvertedToPdf =
      origMime.includes('officedocument.') ||
      origMime === 'application/msword' ||
      origMime === 'application/vnd.ms-powerpoint' ||
      origMime === 'application/vnd.ms-excel' ||
      origMime.includes('opendocument') ||
      origMime.includes('rtf') ||
      /\.(docx?|pptx?|odt|odp|ods|rtf|xlsx?|csv)$/i.test(origName)
    const mime = wasConvertedToPdf ? 'application/pdf' : origMime
    if (previewLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, minHeight: 0 }}>
          <CircularProgress size={24} />
        </Box>
      )
    }
    if (previewError) {
      return (
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{previewError}</Alert>
        </Box>
      )
    }
    if (!previewObjectUrl) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Chưa có dữ liệu preview.
          </Typography>
        </Box>
      )
    }
    if (mime.includes('pdf')) {
      // Khi đang share evidence active + chính file này → dùng presentation viewer
      // (page sync, annotation, presenter claim). Khi chỉ preview cá nhân → iframe.
      const isActiveShare =
        !!courtroom?.shared_evidence_active &&
        courtroom.shared_evidence_asset_id === selectedAsset.id
      if (isActiveShare && courtroom) {
        const isHost =
          canManageCourtrooms ||
          (currentUser?.role === 'judge' &&
            !!courtroom.host_judge_id &&
            courtroom.host_judge_id === currentUser.id)
        return (
          <PdfPresentationViewer
            courtroom={courtroom}
            pdfBlobUrl={previewObjectUrl}
            currentUserId={currentUser?.id}
            isHost={isHost}
          />
        )
      }
      return (
        <Box
          component="iframe"
          src={previewObjectUrl}
          sx={{ width: '100%', flex: 1, minHeight: 0, border: 0 }}
        />
      )
    }
    if (mime.startsWith('image/')) {
      return (
        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}>
          <Box component="img" src={previewObjectUrl} alt={selectedAsset.fileName} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </Box>
      )
    }
    if (mime.startsWith('video/')) {
      return (
        <Box sx={{ p: 1, height: '100%' }}>
          <Box component="video" controls src={previewObjectUrl} sx={{ width: '100%', maxHeight: '100%' }} />
        </Box>
      )
    }
    if (mime.startsWith('audio/')) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            File audio: {selectedAsset.fileName}
          </Typography>
          <Box component="audio" controls src={previewObjectUrl} sx={{ width: '100%' }} />
        </Box>
      )
    }
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Định dạng này chưa hỗ trợ preview trực tiếp.
        </Alert>
        <Button variant="outlined" startIcon={<Download />} onClick={() => window.open(previewObjectUrl, '_blank')}>
          Mở file ở tab mới
        </Button>
      </Box>
    )
  }

  useEffect(() => {
    if (!courtroom?.shared_evidence_active || !courtroom.shared_evidence_asset_id) return
    const sharedId = courtroom.shared_evidence_asset_id
    if (selectedAssetId === sharedId) return
    // Tìm trong local assets trước (admin/owner thường có sẵn).
    let shared = assets.find((a) => a.id === sharedId)
    // Fallback CR#5: nếu Student không có asset này trong list (BE filter theo
    // permission), build synthetic asset từ courtroom.shared_evidence_* để
    // vẫn render được preview (BE download endpoint sẽ tự check quyền view).
    if (!shared && courtroom.shared_evidence_asset_type) {
      const synthetic: CourtAsset = {
        id: sharedId,
        sourceType: (courtroom.shared_evidence_asset_type as CourtAssetType) || 'document',
        title: courtroom.shared_evidence_title || courtroom.shared_evidence_file_name || sharedId,
        fileName: courtroom.shared_evidence_file_name || courtroom.shared_evidence_title || sharedId,
        mimeType: courtroom.shared_evidence_mime_type || 'application/octet-stream',
        uploadedAt: courtroom.shared_started_at || undefined,
        teamId: undefined,
        size: 0,
      }
      shared = synthetic
    }
    if (shared) {
      loadAssetPreview(shared)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    courtroom?.shared_evidence_active,
    courtroom?.shared_evidence_asset_id,
    courtroom?.shared_evidence_asset_type,
    courtroom?.shared_evidence_title,
    courtroom?.shared_evidence_file_name,
    courtroom?.shared_evidence_mime_type,
    assets,
  ])

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Virtual Courtroom"
        subtitle="BR-06-01: Split-screen - trái là Case Content, phải là video participants."
        icon={<VideoCall />}
      />

      {/* Tình tiết bất ngờ — Snackbar top-center, BTC inject qua /inject-surprise.
          Đặt nổi ở top-center + auto-hide 30s để mọi participant đều thấy ngay. */}
      <Snackbar
        open={!!surpriseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setSurpriseAlert(null)}
        autoHideDuration={30000}
        sx={{ mt: 8, zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <Alert
          severity="warning"
          variant="filled"
          icon={<Warning />}
          onClose={() => setSurpriseAlert(null)}
          sx={{
            alignItems: 'flex-start',
            minWidth: 380,
            maxWidth: 560,
            boxShadow: 8,
            fontWeight: 600,
            fontSize: '0.95rem',
            border: '2px solid',
            borderColor: 'warning.dark',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
            ⚡ Tình tiết bất ngờ
            {surpriseAlert?.actorName ? ` — ${surpriseAlert.actorName}` : ''}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {surpriseAlert?.statement}
          </Typography>
          {surpriseAlert?.targetTeamId && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9, fontWeight: 700 }}>
              Đội phải ứng biến:{' '}
              {teams.find((t) => t.id === surpriseAlert.targetTeamId)?.name ||
                surpriseAlert.targetTeamId}
            </Typography>
          )}
        </Alert>
      </Snackbar>

      <Paper
        elevation={0}
        sx={(t) => ({ ...getFilterPaperSx(t), p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } })}
      >
        <Typography variant="h6" gutterBottom sx={{ mb: { xs: 2, sm: 3 }, fontWeight: 600 }}>
          Chọn cuộc thi và vòng tranh tụng online
        </Typography>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="case-select-label">Cuộc thi</InputLabel>
              <Select
                labelId="case-select-label"
                value={selectedCaseId}
                label="Cuộc thi"
                onChange={(e) => {
                  setSelectedCaseId(e.target.value)
                  setSelectedRoundId('')
                }}
              >
                <MenuItem value="">
                  <em>-- Chọn cuộc thi --</em>
                </MenuItem>
                {cases.map((caseItem) => (
                  <MenuItem key={caseItem.id} value={caseItem.id}>
                    {formatCaseDisplay(caseItem)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!selectedCaseId || onlineHearingRounds.length === 0}>
              <InputLabel id="round-select-label">Vòng Bán kết</InputLabel>
              <Select
                labelId="round-select-label"
                value={selectedRoundId}
                label="Vòng Bán kết"
                onChange={(e) => setSelectedRoundId(e.target.value)}
              >
                <MenuItem value="">
                  <em>-- Chọn vòng thi --</em>
                </MenuItem>
                {onlineHearingRounds.map((round) => (
                  <MenuItem key={round.id} value={round.id}>
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {round.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(round.start_date).toLocaleDateString('vi-VN')} - {new Date(round.end_date).toLocaleDateString('vi-VN')}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedCaseId && onlineHearingRounds.length === 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Cuộc thi này chưa có vòng tranh tụng online.
              </Alert>
            )}
          </Grid>
        </Grid>
      </Paper>

      {selectedRoundId && (
        <Box>
          {isLoadingCourtroom ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : !courtroom ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Courtroom chưa được tạo cho vòng thi này.
              </Alert>
              {canManageCourtrooms && (
                <Paper elevation={0} sx={(t) => ({ ...getContentPaperSx(t), p: 2, maxWidth: 560 })}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Tạo courtroom cho vòng đã chọn
                  </Typography>
                  <TextField
                    label="Host GK (user ID — tuỳ chọn)"
                    fullWidth
                    size="small"
                    margin="dense"
                    value={newHostJudgeId}
                    onChange={(e) => setNewHostJudgeId(e.target.value)}
                  />
                  <TextField
                    label="Ghi chú phiên"
                    fullWidth
                    size="small"
                    margin="dense"
                    multiline
                    minRows={2}
                    value={newSessionNotes}
                    onChange={(e) => setNewSessionNotes(e.target.value)}
                  />
                  <Button
                    variant="contained"
                    sx={{ mt: 2, textTransform: 'none' }}
                    disabled={isCreatingCourtroom}
                    onClick={async () => {
                      try {
                        await createCourtroom({
                          round_id: selectedRoundId,
                          host_judge_id: newHostJudgeId.trim() || undefined,
                          session_notes: newSessionNotes.trim() || undefined,
                        }).unwrap()
                        setNewHostJudgeId('')
                        setNewSessionNotes('')
                        refetchCourtroom()
                      } catch (e: any) {
                        alert(e?.data?.detail || e?.message || 'Tạo courtroom thất bại')
                      }
                    }}
                  >
                    {isCreatingCourtroom ? <CircularProgress size={22} /> : 'Tạo courtroom'}
                  </Button>
                </Paper>
              )}
            </Box>
          ) : (
            <Card elevation={0} sx={(t) => ({ ...getCardSurfaceSx(t), borderRadius: 2 })}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Layout chia đôi: Panel trái duyệt tài liệu vụ án và Panel phải cho video call participants.
                </Alert>
                {courtroom.shared_evidence_active && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    [{sharingPresenterLabel}] đang chia sẻ:
                    {' '}
                    {courtroom.shared_evidence_title || courtroom.shared_evidence_file_name || 'chứng cứ'}
                  </Alert>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    mb: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                      {selectedCase?.title || 'Courtroom'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Vòng: {onlineHearingRounds.find((r) => r.id === selectedRoundId)?.name || 'N/A'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <CalendarToday fontSize="small" color="action" />
                      <Typography variant="body2">
                        {courtroom.started_at ? new Date(courtroom.started_at).toLocaleString('vi-VN') : 'Chưa bắt đầu'}
                      </Typography>
                      {courtroom.ended_at && (
                        <Typography variant="body2" color="text.secondary">
                          / Kết thúc: {new Date(courtroom.ended_at).toLocaleString('vi-VN')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Chip label={STATUS_LABELS[courtroom.status]} color={STATUS_COLORS[courtroom.status]} />
                </Box>

                <Box
                  ref={splitWrapRef}
                  sx={{
                    display: isCompact ? 'block' : 'flex',
                    alignItems: isCompact ? undefined : 'stretch',
                    gap: isCompact ? 0 : 1,
                    height: isCompact ? 'auto' : 'calc(100dvh - 320px)',
                    minHeight: isCompact ? 520 : 560,
                  }}
                >
                  {isCompact ? (
                    <Tabs value={activePanelTab} onChange={(_, v) => setActivePanelTab(v)} sx={{ mb: 1 }}>
                      <Tab label="Case Content (CC)" />
                      <Tab label="Video Participants" />
                    </Tabs>
                  ) : null}

                  {(isCompact ? activePanelTab === 0 : true) && (
                    <Paper
                      elevation={0}
                      sx={(t) => ({
                        ...getContentPaperSx(t),
                        width: isCompact ? '100%' : `${splitRatio}%`,
                        height: isCompact ? 560 : '100%',
                        minHeight: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        alignSelf: 'stretch',
                      })}
                    >
                      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Panel trái - Chứng cứ / Hồ sơ (CC)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Scroll độc lập với panel video
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                        }}
                      >
                        <Box sx={{ flex: '42 1 0%', minHeight: 0, overflow: 'auto' }}>
                          <List dense disablePadding>
                            {assets.length === 0 && (
                              <Box sx={{ px: 2, py: 3 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Chưa có hồ sơ/chứng cứ cho vụ án này.
                                </Typography>
                              </Box>
                            )}
                            {assets.map((asset) => {
                              const isActive = selectedAssetId === asset.id
                              const isShared =
                                !!courtroom.shared_evidence_active &&
                                courtroom.shared_evidence_asset_id === asset.id
                              const icon = asset.mimeType.includes('pdf')
                                ? <Description fontSize="small" />
                                : asset.mimeType.startsWith('image/')
                                  ? <Image fontSize="small" />
                                  : asset.mimeType.startsWith('audio/')
                                    ? <Audiotrack fontSize="small" />
                                    : asset.mimeType.startsWith('video/')
                                      ? <Movie fontSize="small" />
                                      : <Description fontSize="small" />
                              return (
                                <ListItemButton
                                  key={`${asset.sourceType}:${asset.id}`}
                                  selected={isActive}
                                  onClick={() => loadAssetPreview(asset)}
                                  sx={{ px: 1.5, py: 1 }}
                                >
                                  <Box sx={{ mr: 1, color: 'text.secondary' }}>{icon}</Box>
                                  <ListItemText
                                    primary={asset.title}
                                    secondary={`${asset.fileName} • ${formatBytes(asset.size)}${asset.teamId ? ` • ${asset.teamId}` : ''}`}
                                    primaryTypographyProps={{ noWrap: true, fontSize: 14, fontWeight: isActive ? 700 : 500 }}
                                    secondaryTypographyProps={{ noWrap: true, fontSize: 12 }}
                                  />
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {isShared && <Chip size="small" color="warning" label="Đang share" />}
                                    {canShareEvidence && courtroom.status === CourtroomStatus.ACTIVE && (
                                      <Button
                                        size="small"
                                        variant={isShared ? 'outlined' : 'text'}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          handleShareEvidence(asset)
                                        }}
                                        disabled={isSharingEvidence}
                                        sx={{ textTransform: 'none' }}
                                      >
                                        Share
                                      </Button>
                                    )}
                                  </Box>
                                </ListItemButton>
                              )
                            })}
                          </List>
                        </Box>
                        <Divider />
                        <Box
                          ref={previewSurfaceRef}
                          onMouseMove={handlePointerMove}
                          sx={{
                            flex: '58 1 0%',
                            minHeight: 0,
                            overflow: 'hidden',
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <Box
                            ref={previewWrapRef}
                            sx={{
                              flex: 1,
                              minHeight: 0,
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              position: 'relative',
                              bgcolor: 'background.paper',
                              // Fullscreen: iframe/img/video bên trong scale theo viewport
                              ...(fullscreenEl === 'preview' && {
                                width: '100vw',
                                height: '100vh',
                                bgcolor: '#000',
                              }),
                              // Mọi iframe/img/video con đều fill cha khi fullscreen
                              '&:fullscreen': {
                                width: '100vw',
                                height: '100vh',
                                bgcolor: '#000',
                              },
                              '&:fullscreen iframe, &:fullscreen img, &:fullscreen video': {
                                width: '100% !important',
                                height: '100% !important',
                                maxWidth: 'none !important',
                                maxHeight: 'none !important',
                                objectFit: 'contain',
                              },
                            }}
                          >
                            {renderPreview()}
                            {selectedAsset && (
                              <Tooltip title="Toàn màn hình">
                                <IconButton
                                  size="small"
                                  onClick={() => toggleFullscreen('preview')}
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    zIndex: 20,
                                    bgcolor: 'rgba(0,0,0,0.55)',
                                    color: '#fff',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                                  }}
                                >
                                  {fullscreenEl === 'preview' ? (
                                    <FullscreenExit fontSize="small" />
                                  ) : (
                                    <Fullscreen fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                            {/* Pointer overlay PHẢI nằm INSIDE previewWrapRef để khi
                                fullscreen vẫn nhìn thấy + tọa độ % vẫn relative đúng. */}
                            {remotePointer?.active && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: `${Math.max(0, Math.min(1, remotePointer.x)) * 100}%`,
                                  top: `${Math.max(0, Math.min(1, remotePointer.y)) * 100}%`,
                                  transform: 'translate(-50%, -50%)',
                                  pointerEvents: 'none',
                                  zIndex: 30,
                                  width: 14,
                                  height: 14,
                                  borderRadius: '50%',
                                  bgcolor: 'error.main',
                                  boxShadow: '0 0 0 5px rgba(244,67,54,0.25)',
                                }}
                              />
                            )}
                            {remotePointer?.active && (
                              <Chip
                                size="small"
                                color="error"
                                label={`${remotePointer.byRole || 'Judge'} đang chỉ`}
                                sx={{ position: 'absolute', left: 8, top: 8, zIndex: 31 }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  )}

                  {!isCompact && (
                    <Box
                      onMouseDown={() => setDraggingDivider(true)}
                      sx={{
                        width: 8,
                        borderRadius: 1,
                        cursor: 'col-resize',
                        bgcolor: draggingDivider ? 'primary.main' : 'divider',
                        opacity: draggingDivider ? 0.6 : 1,
                        transition: 'all .12s ease',
                      }}
                      aria-label="Kéo để chỉnh tỷ lệ panel"
                    />
                  )}

                  {(isCompact ? activePanelTab === 1 : true) && (
                    <Paper
                      elevation={0}
                      sx={(t) => ({
                        ...getContentPaperSx(t),
                        width: isCompact ? '100%' : `${100 - splitRatio}%`,
                        height: isCompact ? 560 : '100%',
                        minHeight: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        alignSelf: 'stretch',
                      })}
                    >
                      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          Panel phải - Video Call (Daily.co)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Daily xử lý audio/video, eCourt xử lý share chứng cứ và điều phối phiên
                        </Typography>
                      </Box>

                      <Box sx={{ p: 2, overflow: 'auto', flex: 1, minHeight: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                          <Chip icon={<People />} label={`${participantInfos.length} người tham gia`} color="primary" variant="outlined" />
                          <Chip label={`Trạng thái phòng: ${STATUS_LABELS[courtroom.status]}`} color={STATUS_COLORS[courtroom.status]} />
                          <Chip
                            label={`Thời gian còn lại: ${timerLabel}${timerState?.is_paused ? ' (Paused)' : ''}`}
                            color={timerSeverity}
                            variant={timerSeverity === 'success' ? 'outlined' : 'filled'}
                          />
                          {courtroom.recording_status === 'recording' && (
                            <Chip
                              icon={<RadioButtonChecked />}
                              label="REC đang ghi hình"
                              color="error"
                              sx={{ fontWeight: 700 }}
                            />
                          )}
                          {courtroom.recording_status === 'processing' && (
                            <Chip
                              label="Đang xử lý video..."
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Box>
                        {timerState?.remaining_seconds === 600 && (
                          <Alert severity="warning" sx={{ mb: 1 }}>Còn 10 phút.</Alert>
                        )}
                        {timerState?.remaining_seconds === 300 && (
                          <Alert severity="warning" sx={{ mb: 1 }}>Còn 5 phút, vui lòng chuẩn bị kết luận.</Alert>
                        )}
                        {timerState?.remaining_seconds === 60 && (
                          <Alert severity="error" sx={{ mb: 1 }}>Còn 1 phút!</Alert>
                        )}
                        {timerState?.remaining_seconds === 0 && (
                          <Alert severity="error" sx={{ mb: 1 }}>Hết giờ.</Alert>
                        )}

                        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {courtroom.meeting_link ? (
                            meetingProvider !== 'daily' ? (
                              <Tooltip
                                title={
                                  isUserKicked
                                    ? `Bạn đã bị Chủ tọa loại khỏi phiên này.${
                                        myKickInfo?.reason ? ` Lý do: ${myKickInfo.reason}` : ''
                                      }`
                                    : ''
                                }
                                disableHoverListener={!isUserKicked}
                              >
                                <span>
                                  <Button
                                    variant="contained"
                                    startIcon={<OpenInNew />}
                                    onClick={handleJoinCourtroom}
                                    disabled={
                                      isJoining ||
                                      isUserKicked ||
                                      courtroom.status === CourtroomStatus.COMPLETED ||
                                      courtroom.status === CourtroomStatus.CANCELLED
                                    }
                                    sx={{
                                      background: gradientBrand,
                                      '&:hover': { background: gradientBrand, filter: 'brightness(1.06)' },
                                      textTransform: 'none',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {isJoining ? <CircularProgress size={18} /> : joinButtonLabel}
                                  </Button>
                                </span>
                              </Tooltip>
                            ) : null
                          ) : (
                            <Alert severity="warning">Chưa có meeting link cho courtroom này.</Alert>
                          )}
                        </Box>
                        {meetingProvider !== 'daily' ? (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            Phiên này đang dùng link legacy không phải Daily.co nên không nhúng trực tiếp. Vui lòng mở bằng tab mới hoặc cập nhật lại meeting link sang Daily.
                          </Alert>
                        ) : (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            Daily.co xử lý audio/video phiên tòa. Mọi thao tác mở/chia sẻ chứng cứ thực hiện tại panel trái của eCourt để đồng bộ nghiệp vụ.
                          </Alert>
                        )}
                        {courtroom.status === CourtroomStatus.ACTIVE && canEmbedMeeting && !dailyEmbedFailed && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            <strong>Tiết kiệm chi phí Daily:</strong> nếu không tươ tác với trang này trong khoảng{' '}
                            {Math.round(COURTROOM_IDLE_MS.warnAfterMs / 60000)} phút, hệ thống sẽ cảnh báo; sau thêm{' '}
                            {Math.round(COURTROOM_IDLE_MS.graceAfterWarnMs / 60000)} phút nếu bạn không xác nhận vẫn
                            tham gia thì kết nối video sẽ tự ngắt. Chủ động rời phòng khi không cần (nút rời phòng trên
                            khung Daily).
                          </Alert>
                        )}
                        {courtroom.meeting_link && canEmbedMeeting && !dailyEmbedFailed && (
                          <Paper
                            ref={meetWrapRef}
                            variant="outlined"
                            sx={{
                              mb: 2,
                              borderRadius: 2,
                              overflow: 'hidden',
                              position: 'relative',
                              // Fullscreen: lấp đầy viewport
                              ...(fullscreenEl === 'meet' && {
                                mb: 0,
                                borderRadius: 0,
                                width: '100vw',
                                height: '100vh',
                                bgcolor: '#000',
                                display: 'flex',
                                flexDirection: 'column',
                              }),
                              '&:fullscreen': {
                                width: '100vw',
                                height: '100vh',
                                bgcolor: '#000',
                                display: 'flex',
                                flexDirection: 'column',
                              },
                              '&:fullscreen iframe': {
                                width: '100% !important',
                                height: '100% !important',
                                flex: 1,
                              },
                            }}
                          >
                            <Box
                              sx={{
                                px: 1.5,
                                py: 1,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                Daily Room (embedded)
                              </Typography>
                              <Tooltip title="Toàn màn hình phòng họp">
                                <IconButton
                                  size="small"
                                  onClick={() => toggleFullscreen('meet')}
                                >
                                  {fullscreenEl === 'meet' ? (
                                    <FullscreenExit fontSize="small" />
                                  ) : (
                                    <Fullscreen fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Box
                              ref={dailyEmbedRef}
                              sx={{
                                width: '100%',
                                minHeight: 420,
                                // Khi Paper cha fullscreen → iframe Daily fill toàn viewport
                                ...(fullscreenEl === 'meet' && {
                                  minHeight: 0,
                                  height: 'calc(100vh - 49px)',  // trừ header bar
                                }),
                              }}
                            />
                          </Paper>
                        )}
                        {courtroom.meeting_link && canEmbedMeeting && dailyEmbedFailed && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            Trình duyệt hoặc extension đang chặn WebRTC nên không nhúng được Daily. Thử trình duyệt
                            khác, tắt extension chặn iframe/WebRTC, hoặc kiểm tra chính sách mạng — hệ thống không hỗ
                            trợ mở Daily qua tab mới từ đây.
                          </Alert>
                        )}

                        {/* Biên bản phiên + Macro timeline — accordion thu gọn */}
                        <CourtroomTimelinePanel
                          courtroomId={courtroom.id}
                          caseId={courtroom.case_id}
                          roundId={courtroom.round_id}
                          canModerate={canManageCourtrooms}
                        />

                        {/* Q&A moderation cho judge/admin/moderator livestream */}
                        {(canManageCourtrooms ||
                          currentUser?.role === 'judge' ||
                          (currentUser &&
                            (courtroom.livestream_moderator_ids || []).includes(
                              currentUser.id,
                            ))) && (
                          <ModeratorQAPanel
                            courtroomId={courtroom.id}
                            livestreamEnabled={!!courtroom.livestream_enabled}
                          />
                        )}

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: 1.5,
                          }}
                        >
                          {participantInfos.map((p) => (
                            <Card key={p.userId} variant="outlined" sx={{ borderRadius: 2 }}>
                              <CardContent sx={{ p: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Avatar sx={{ width: 36, height: 36 }}>{p.avatarText}</Avatar>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={700} noWrap>
                                      {p.displayName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {p.roleLabel}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Chip
                                    size="small"
                                    icon={p.micOn ? <Mic /> : <MicOff />}
                                    label={p.micOn ? 'Mic on' : 'Mic off'}
                                    color={p.micOn ? 'success' : 'default'}
                                    variant="outlined"
                                  />
                                  <Chip
                                    size="small"
                                    icon={p.camOn ? <Videocam /> : <VideocamOff />}
                                    label={p.camOn ? 'Cam on' : 'Cam off'}
                                    color={p.camOn ? 'success' : 'default'}
                                    variant="outlined"
                                  />
                                </Box>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                  {p.canMute && !p.isMuted && (
                                    <Button
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      startIcon={<MicOff />}
                                      onClick={async () => {
                                        try {
                                          await muteParticipant({
                                            courtroomId: courtroom.id,
                                            userId: p.userId,
                                          }).unwrap()
                                        } catch (err: any) {
                                          alert(`Mute thất bại: ${err?.data?.detail || err?.message}`)
                                        }
                                      }}
                                      sx={{ textTransform: 'none' }}
                                    >
                                      Mute
                                    </Button>
                                  )}
                                  {p.canMute && p.isMuted && (
                                    <Button
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      startIcon={<Mic />}
                                      onClick={async () => {
                                        try {
                                          await unmuteParticipant({
                                            courtroomId: courtroom.id,
                                            userId: p.userId,
                                          }).unwrap()
                                        } catch (err: any) {
                                          alert(`Unmute thất bại: ${err?.data?.detail || err?.message}`)
                                        }
                                      }}
                                      sx={{ textTransform: 'none' }}
                                    >
                                      Unmute
                                    </Button>
                                  )}
                                  {p.canKick && (
                                    <Button
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      onClick={() => {
                                        setParticipantToKick(p)
                                        setKickReason('')
                                        setKickDialogOpen(true)
                                      }}
                                      sx={{ textTransform: 'none' }}
                                    >
                                      Kick
                                    </Button>
                                  )}
                                </Stack>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>

                        {participantInfos.length === 0 && (
                          <Alert severity="info" sx={{ mt: 1 }}>
                            Chưa có participant nào join vào phòng.
                          </Alert>
                        )}

                        {/* Danh sách đã bị loại — Admin/Host có thể cho quay lại */}
                        {canControlSession && courtroom.kicked_participants && (() => {
                          const kickedList = Object.entries(courtroom.kicked_participants || {})
                            .filter(([, info]) => info?.status === 'kicked')
                          if (kickedList.length === 0) return null
                          return (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" gutterBottom>
                                Đã loại khỏi phiên ({kickedList.length})
                              </Typography>
                              <Stack direction="column" spacing={1}>
                                {kickedList.map(([uid, info]) => {
                                  const u = users.find((x: any) => x.id === uid)
                                  const displayName = u?.full_name || u?.username || uid
                                  return (
                                    <Card key={uid} variant="outlined" sx={{ borderColor: 'error.light' }}>
                                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                          <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="body2" fontWeight={500} noWrap>
                                              {displayName}
                                            </Typography>
                                            {info?.reason && (
                                              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic' }}>
                                                Lý do: {info.reason}
                                              </Typography>
                                            )}
                                          </Box>
                                          <Button
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            onClick={async () => {
                                              try {
                                                await unkickParticipant({
                                                  courtroomId: courtroom.id,
                                                  userId: uid,
                                                }).unwrap()
                                                refetchCourtroom()
                                              } catch (err: any) {
                                                alert(`Khôi phục thất bại: ${err?.data?.detail || err?.message}`)
                                              }
                                            }}
                                            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                                          >
                                            Cho quay lại
                                          </Button>
                                        </Box>
                                      </CardContent>
                                    </Card>
                                  )
                                })}
                              </Stack>
                            </Box>
                          )
                        })()}

                        <Divider sx={{ my: 2 }} />
                        {courtroom.shared_evidence_active && (
                          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              color="warning"
                              label={`Đang chia sẻ: ${courtroom.shared_evidence_title || courtroom.shared_evidence_file_name || 'chứng cứ'}`}
                            />
                            {(courtroom.shared_by_user_id === currentUser?.id || canManageCourtrooms) && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={handleStopSharing}
                                disabled={isStoppingSharing}
                                sx={{ textTransform: 'none' }}
                              >
                                Stop sharing
                              </Button>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  )}
                </Box>
              </CardContent>

              <CardActions sx={{ p: { xs: 1.5, sm: 2 }, pt: 0, justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 0 }, alignItems: { xs: 'stretch', sm: 'center' } }}>
                <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  {courtroom.meeting_link && meetingProvider !== 'daily' && (
                    <Tooltip
                      title={
                        isUserKicked
                          ? `Bạn đã bị Chủ tọa loại khỏi phiên này.${
                              myKickInfo?.reason ? ` Lý do: ${myKickInfo.reason}` : ''
                            }`
                          : ''
                      }
                      disableHoverListener={!isUserKicked}
                    >
                      <span>
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<OpenInNew />}
                          onClick={handleJoinCourtroom}
                          disabled={
                            isJoining ||
                            isUserKicked ||
                            courtroom.status === CourtroomStatus.COMPLETED ||
                            courtroom.status === CourtroomStatus.CANCELLED
                          }
                          sx={{
                            background: gradientBrand,
                            '&:hover': { background: gradientBrand, filter: 'brightness(1.06)' },
                            textTransform: 'none',
                            fontWeight: 600,
                            width: { xs: '100%', sm: 'auto' },
                          }}
                        >
                          {isJoining ? <CircularProgress size={20} /> : joinButtonLabel}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
                  {canControlSession && (
                      <TextField
                        type="number"
                        size="small"
                        label="Phút"
                        value={sessionDurationMinutes}
                        onChange={(e) => setSessionDurationMinutes(Number(e.target.value || 45))}
                        sx={{ width: { xs: '100%', sm: 100 } }}
                        inputProps={{ min: 1, max: 240 }}
                      />
                    )}
                  {canControlSession &&
                    (courtroom.status === CourtroomStatus.SCHEDULED || courtroom.status === CourtroomStatus.ACTIVE) && (
                      <Button
                        variant="outlined"
                        onClick={handleConfigureTimer}
                        disabled={isConfiguringTimer}
                        sx={{ textTransform: 'none', fontWeight: 500, width: { xs: '100%', sm: 'auto' } }}
                      >
                        {isConfiguringTimer ? <CircularProgress size={20} /> : 'Set timer'}
                      </Button>
                    )}
                  {canControlSession &&
                    courtroom.status === CourtroomStatus.ACTIVE && (
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={handlePauseResumeTimer}
                        disabled={isPausingTimer || isResumingTimer}
                        sx={{ textTransform: 'none', fontWeight: 500, width: { xs: '100%', sm: 'auto' } }}
                      >
                        {(isPausingTimer || isResumingTimer)
                          ? <CircularProgress size={20} />
                          : timerState?.is_paused ? 'Resume timer' : 'Pause timer'}
                      </Button>
                    )}
                  {canUsePointer && (
                    <Button
                      variant={pointerMode ? 'contained' : 'outlined'}
                      color={pointerMode ? 'error' : 'inherit'}
                      startIcon={<AdsClick />}
                      onClick={handleTogglePointerMode}
                      sx={{ textTransform: 'none', fontWeight: 500, width: { xs: '100%', sm: 'auto' } }}
                    >
                      {pointerMode ? 'Tắt Pointer' : 'Bật Pointer'}
                    </Button>
                  )}
                  {/* UAT-Chung kết #14: BTC inject tình tiết bất ngờ */}
                  {canManageCourtrooms && courtroom.status === CourtroomStatus.ACTIVE && (
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={() => setSurpriseOpen(true)}
                      sx={{ textTransform: 'none', fontWeight: 500, width: { xs: '100%', sm: 'auto' } }}
                    >
                      Tình tiết bất ngờ
                    </Button>
                  )}
                  {canControlSession &&
                    courtroom.status === CourtroomStatus.ACTIVE &&
                    courtroom.recording_status !== 'recording' && (
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<RadioButtonChecked />}
                        onClick={handleStartRecording}
                        disabled={isStartingRecording}
                        sx={{ textTransform: 'none', fontWeight: 500, width: { xs: '100%', sm: 'auto' } }}
                      >
                        {isStartingRecording ? <CircularProgress size={20} /> : 'Bắt đầu ghi hình'}
                      </Button>
                    )}
                  {canControlSession &&
                    courtroom.status === CourtroomStatus.ACTIVE &&
                    courtroom.recording_status === 'recording' && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<Stop />}
                        onClick={handleStopRecording}
                        disabled={isStoppingRecording}
                        sx={{ textTransform: 'none', fontWeight: 500, width: { xs: '100%', sm: 'auto' } }}
                      >
                        {isStoppingRecording ? <CircularProgress size={20} /> : 'Dừng ghi hình'}
                      </Button>
                    )}
                  {canControlSession &&
                    courtroom.status === CourtroomStatus.SCHEDULED && (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<VideoCall />}
                        onClick={handleStartCourtroom}
                        disabled={isStarting}
                        sx={{ textTransform: 'none', fontWeight: 500, width: { xs: '100%', sm: 'auto' } }}
                      >
                        {isStarting ? <CircularProgress size={20} /> : 'Bắt đầu phiên'}
                      </Button>
                    )}
                  {canControlSession &&
                    courtroom.status === CourtroomStatus.ACTIVE && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Stop />}
                      onClick={() => setEndDialogOpen(true)}
                      disabled={isEnding}
                      sx={{ textTransform: 'none', fontWeight: 500, width: { xs: '100%', sm: 'auto' } }}
                    >
                      {isEnding ? <CircularProgress size={20} /> : 'Kết thúc Courtroom'}
                    </Button>
                  )}
                  {canManageCourtrooms && courtroom.status !== CourtroomStatus.CANCELLED && (
                    <Button
                      variant="outlined"
                      startIcon={courtroom.recording_url ? <EditIcon /> : <LinkIcon />}
                      onClick={() => handleOpenRecordingDialog(courtroom.id)}
                      sx={{ textTransform: 'none', fontWeight: 500, width: { xs: '100%', sm: 'auto' } }}
                    >
                      {courtroom.recording_url ? 'Cập nhật Recording URL' : 'Thêm Recording URL'}
                    </Button>
                  )}
                  {canControlSession &&
                    courtroom.status === CourtroomStatus.COMPLETED &&
                    (courtroom.verdict_status || 'none') === 'none' && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setVerdictDialogOpen(true)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 500,
                          width: { xs: '100%', sm: 'auto' },
                        }}
                      >
                        Nhập phán quyết
                      </Button>
                    )}
                  {canManageCourtrooms &&
                    courtroom.status === CourtroomStatus.COMPLETED &&
                    (courtroom.verdict_status || 'none') === 'submitted' && (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handlePublishVerdict}
                        disabled={isPublishingVerdict}
                        sx={{ textTransform: 'none', fontWeight: 500, width: { xs: '100%', sm: 'auto' } }}
                      >
                        {isPublishingVerdict ? <CircularProgress size={20} /> : 'Công bố phán quyết'}
                      </Button>
                    )}
                </Box>
              </CardActions>
              {(courtroom.recording_url || courtroom.recording_status === 'ready') && (() => {
                const recUrl = courtroom.recording_url || ''
                const recFormat = (courtroom.recording_format || '').toLowerCase()
                const lowerUrl = recUrl.toLowerCase()
                const isPlayableExt =
                  /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(lowerUrl) ||
                  ['mp4', 'webm', 'mov', 'm4v', 'ogg'].includes(recFormat)
                // Drive/external link → để user mở tab mới, không thử embed (CORS / auth).
                const isExternalView =
                  /drive\.google\.com|youtube\.com|youtu\.be|vimeo\.com/i.test(lowerUrl)
                const canEmbedVideo = !!recUrl && isPlayableExt && !isExternalView
                return (
                  <Box sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                    <Alert severity="info" sx={{ mb: canEmbedVideo || recUrl ? 1.5 : 0 }}>
                      Recording: {courtroom.recording_format || 'mp4'}
                      {typeof courtroom.recording_duration_seconds === 'number'
                        ? ` • ${courtroom.recording_duration_seconds}s`
                        : ''}
                      {typeof courtroom.recording_size_bytes === 'number'
                        ? ` • ${Math.round(courtroom.recording_size_bytes / 1024 / 1024)}MB`
                        : ''}
                    </Alert>
                    {canEmbedVideo && (
                      <Box
                        sx={{
                          borderRadius: 2,
                          overflow: 'hidden',
                          bgcolor: 'black',
                          aspectRatio: '16 / 9',
                        }}
                      >
                        <Box
                          component="video"
                          controls
                          preload="metadata"
                          src={recUrl}
                          sx={{ width: '100%', height: '100%', display: 'block' }}
                        >
                          Trình duyệt không hỗ trợ video. Tải xuống:{' '}
                          <a href={recUrl} target="_blank" rel="noopener noreferrer">
                            {recUrl}
                          </a>
                        </Box>
                      </Box>
                    )}
                    {!canEmbedVideo && recUrl && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<OpenInNew />}
                        component="a"
                        href={recUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: 'none' }}
                      >
                        Mở recording (tab mới)
                      </Button>
                    )}
                  </Box>
                )
              })()}
              {courtroom.status === CourtroomStatus.COMPLETED && verdict?.status && verdict.status !== 'none' && (
                <Box sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                  <Alert severity={verdict.status === 'published' ? 'success' : 'info'}>
                    Phán quyết: {verdict.status === 'published' ? 'Đã công bố' : 'Chờ công bố'} - {verdict.summary || 'Đã có nội dung'}
                  </Alert>
                </Box>
              )}
            </Card>
          )}
        </Box>
      )}

      <Dialog open={recordingDialogOpen} onClose={() => setRecordingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Thêm Recording URL</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Recording URL"
            value={recordingUrl}
            onChange={(e) => setRecordingUrl(e.target.value)}
            placeholder="https://drive.google.com/..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordingDialogOpen(false)}>Hủy</Button>
          <Button onClick={handleSetRecording} variant="contained" disabled={isSettingRecording || !recordingUrl.trim()}>
            {isSettingRecording ? <CircularProgress size={20} /> : 'Lưu'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={endDialogOpen} onClose={() => setEndDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Kết quả hòa giải</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="mediation-result-label">Kết quả</InputLabel>
            <Select
              labelId="mediation-result-label"
              value={mediationResult}
              label="Kết quả"
              onChange={(e) => setMediationResult(e.target.value as 'mediation_success' | 'mediation_failed' | 'postponed')}
            >
              <MenuItem value="mediation_success">Hòa giải thành công</MenuItem>
              <MenuItem value="mediation_failed">Hòa giải thất bại</MenuItem>
              <MenuItem value="postponed">Hoãn (postponed)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            required
            label="Kết luận của Chủ tọa"
            value={mediationConclusion}
            onChange={(e) => setMediationConclusion(e.target.value)}
            multiline
            minRows={3}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Document ID đính kèm (tuỳ chọn)"
            value={mediationAttachmentId}
            onChange={(e) => setMediationAttachmentId(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndDialogOpen(false)}>Hủy</Button>
          <Button variant="contained" color="error" onClick={handleEndCourtroom} disabled={isEnding || !mediationConclusion.trim()}>
            {isEnding ? <CircularProgress size={20} /> : 'Xác nhận kết thúc phiên'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={verdictDialogOpen} onClose={() => setVerdictDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Nhập phán quyết</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            required
            label="Phán quyết tóm tắt"
            value={verdictSummary}
            onChange={(e) => setVerdictSummary(e.target.value)}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            required
            multiline
            minRows={5}
            label="Phán quyết chi tiết (Rich text dạng HTML)"
            value={verdictDetailHtml}
            onChange={(e) => setVerdictDetailHtml(e.target.value)}
            sx={{ mt: 2 }}
          />
          {/* Dropdown thay text raw — chỉ liệt kê đội thuộc case của phiên. */}
          <FormControl fullWidth required sx={{ mt: 2 }}>
            <InputLabel id="verdict-winner-label">Bên thắng</InputLabel>
            <Select
              labelId="verdict-winner-label"
              label="Bên thắng"
              value={verdictWinnerTeamId}
              onChange={(e) => setVerdictWinnerTeamId(e.target.value as string)}
            >
              {(selectedCase?.assigned_team_ids || [])
                .map((tid) => teams.find((t) => t.id === tid))
                .filter((t): t is NonNullable<typeof t> => !!t)
                .map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>({t.university})</Typography>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Mức bồi thường/hình phạt"
            value={verdictPenalty}
            onChange={(e) => setVerdictPenalty(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Căn cứ pháp lý"
            value={verdictLegalBasis}
            onChange={(e) => setVerdictLegalBasis(e.target.value)}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Document ID đính kèm (tuỳ chọn)"
            value={verdictAttachmentId}
            onChange={(e) => setVerdictAttachmentId(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerdictDialogOpen(false)}>Hủy</Button>
          <Button variant="contained" onClick={handleSubmitVerdict} disabled={isSubmittingVerdict}>
            {isSubmittingVerdict ? <CircularProgress size={20} /> : 'Xác nhận phán quyết'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={idleDailyDisconnect.warnModalOpen} onClose={() => {}} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle>Phát hiện không tương tác (treo máy / rời bàn)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Bạn đã lâu không thao tác trên trang phiên tòa. Daily vẫn tính phí theo thời gian bạn còn trong phòng — để
            tránh tích phí khi không dùng, hệ thống sẽ <strong>tự ngắt video</strong> sau{' '}
            <strong>{idleDailyDisconnect.secondsUntilLeave}</strong> giây nữa.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Nếu bạn vẫn đang theo dõi phiên, hãy bấm &quot;Tôi vẫn tham gia&quot; (hoặc thao tác trên trang trước khi hết
            thời gian).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={idleDailyDisconnect.acknowledgeIdle}>
            Tôi vẫn tham gia
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={kickDialogOpen} onClose={() => setKickDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Xác nhận loại participant</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Bạn có chắc muốn loại {participantToKick?.displayName || 'participant'} khỏi phiên?
          </Typography>
          <TextField
            fullWidth
            label="Lý do kick (tuỳ chọn)"
            value={kickReason}
            onChange={(e) => setKickReason(e.target.value)}
            multiline
            minRows={2}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKickDialogOpen(false)}>Hủy</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleKickParticipant}
            disabled={isKickingParticipant || !participantToKick}
          >
            {isKickingParticipant ? <CircularProgress size={18} /> : 'Xác nhận Kick'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* UAT-Chung kết #14: Dialog Inject tình tiết bất ngờ */}
      <Dialog open={surpriseOpen} onClose={() => setSurpriseOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Tình tiết bất ngờ (BTC Actor)</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Phát một tình tiết bất ngờ trong phiên — đội phải ứng biến tại chỗ.
            Nội dung sẽ được phát realtime + lưu vào macro events + gửi notification cho đội mục tiêu.
          </Alert>
          <TextField
            label="Vai diễn (Actor)"
            value={surpriseActor}
            onChange={(e) => setSurpriseActor(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            placeholder='VD: "Ông Nguyễn Văn Minh"'
            required
          />
          <TextField
            label="Tình tiết / lời khai"
            value={surpriseStatement}
            onChange={(e) => setSurpriseStatement(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            sx={{ mb: 2 }}
            placeholder='VD: "Tôi đã chuyển khoản 2tr ngày 15/3, không nhận được biên lai"'
            required
          />
          <FormControl fullWidth size="small">
            <InputLabel>Đội mục tiêu (để trống = tất cả)</InputLabel>
            <Select
              value={surpriseTargetTeam}
              label="Đội mục tiêu (để trống = tất cả)"
              onChange={(e) => setSurpriseTargetTeam(e.target.value)}
            >
              <MenuItem value=""><em>Tất cả các đội trong vụ</em></MenuItem>
              {(teams || []).map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSurpriseOpen(false)}>Huỷ</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={isInjectingSurprise || !surpriseActor.trim() || !surpriseStatement.trim()}
            onClick={async () => {
              if (!courtroom?.id) return
              try {
                await injectSurprise({
                  courtroomId: courtroom.id,
                  actor_name: surpriseActor.trim(),
                  statement: surpriseStatement.trim(),
                  target_team_id: surpriseTargetTeam || undefined,
                }).unwrap()
                setSurpriseOpen(false)
                setSurpriseActor('')
                setSurpriseStatement('')
                setSurpriseTargetTeam('')
              } catch (e: any) {
                alert(e?.data?.detail || e?.message || 'Inject thất bại')
              }
            }}
          >
            {isInjectingSurprise ? <CircularProgress size={18} /> : 'Phát tình tiết'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}
